import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory, useLocation } from 'react-router-dom'
import { withStyles } from '@aura/components/styles'
import { Box, Grid } from '@aura/components'
import { useTranslation } from 'react-i18next'
import ClientServicingFactorTypes from '../../../../constants/client-servicing-factor-types'
import FetchStatus from '../../../../constants/fetch-migrate'
import FetchAccountRecovery from '../../../../constants/fetch-account-recovery'
import ResendCodeTypes from '../../../../constants/resend-code'
import SubmitPasswordTypes from '../../../../constants/password'
import SubmitVerificationTypes from '../../../../constants/verification'
import SubmitVerifyAuthenticatorCodeTypes from '../../../../constants/verify-authenticator-code'
import { LOGIN_ROUTE } from '../../../../constants/routes'
import UserStatus from '../../../../constants/userStatus'
import ErrorPage from '../../../../components/error-page'
import AppBar from '../../../../components/app-bar-new'
import Footer from '../../../../components/new-footer'
import CustomLoader from '../../../../components/custom-loader'
import SessionTimeout from '../../../../components/session-timeout'
import AuthenticatorCode from '../authenticatorCode'
import RecoverySelection from '../recoverySelection'
import PhoneSetup from '../phoneSetup'
import RegistrationBar from '../registration-bar'
import Authenticators from '../authenticators'
import DeviceType from '../deviceType'
import NewWelcome from '../newWelcome'
import QrCode from '../qrCode'
import QrCodeCannotScan from '../qrCodeCannotScan'
import SetPassword from '../password'
import SignIn from '../signIn'
import Verification from '../verification'
import LogoutStatus from '../../../../constants/logout-status'
import { useConfig } from '../../../../utils/config'
import { clearCookies } from '../../../../utils/cookie'
import styles from './styles'


const Component = ({
    activeRegSession,
    authenticatorStatus,
    changeStep,
    classes,
    dataAuthenticator,
    dataQRCode,
    dataSendChallenge,
    dataUserApps,
    dataUser,
    error,
    errorToken,
    init,
    initAuthenticators,
    getMFAOptionsStatus,
    initQRCode,
    initSendChallenge,
    initUserApps,
    mfaRequired,
    onAfterVerifyAuthenticatorCodeSuccess,
    onBack,
    onBackTwoSteps,
    onBackToAuthenticators,
    onEnroll,
    onFinish,
    onQRCodeNext,
    onQRCodeCannotScan,
    onQRCodeCannotScanNext,
    onResendCode,
    onSetAuthenticator,
    onSetDeviceType,
    onSubmitPassword,
    onSubmitVerification,
    onVerifyAuthenticatorCode,
    onWelcomeNext,
    qrCodeStatus,
    resendSuccessMsg,
    sendChallengeStatus,
    sessionTimeout,
    status,
    stepNumber,
    submitStatusPassword,
    submitStatusResendCode,
    submitStatusVerification,
    submitStatusVerifyAuthenticatorStatus,
    userAppsStatus,
    getAccountRecovery,
    accountRecoveryData,
    accountRecoveryStatus,
    onAccountRecoverySelection,
    onRequestPhoneCodes,
    onSetupRecoveryPhone,
    phoneCodes,
    setupRecoveryPhoneStatus,
    setupRecoveryPhoneError,
    logoutOktaSession,
    terminateOAGSession,
    logoutStatusOkta,
    logoutStatusOAG
}) => {
    const history = useHistory()
    if (errorToken === UserStatus.ACTIVE) {
        history.push(LOGIN_ROUTE)
    }
    const location = useLocation()
    const { t: translate, i18n } = useTranslation()
    const params = new URLSearchParams(location.search)
    const activationToken = params.get('activationToken')
    const language = params.get('language')
    const config = useConfig()
    useEffect(() => {
        i18n.changeLanguage(language)
    }, [])

    const handleLogout = useCallback(() => {
        if (config?.logoutOAGSession) {
            terminateOAGSession(config?.logoutOAGSession)
        }
    }, [])
        
    useEffect(() => {
        if (config?.oktaCurrentSession) {
            logoutOktaSession(config?.oktaCurrentSession)
        }
                
    }, [])
            
    useEffect(() => {
        if (logoutStatusOkta === LogoutStatus.SUCCESS || logoutStatusOkta === LogoutStatus.ERROR) {
            handleLogout()
        }
    }, [ logoutStatusOkta ])
            
    useEffect(() => {
        if (logoutStatusOAG === LogoutStatus.SUCCESS || logoutStatusOAG === LogoutStatus.ERROR) {
            clearCookies(config?.cmxClean, 'OAG cookies')
            clearCookies(config?.secure2Clean, 'OAG secure2 cookies')
            clearCookies(config?.protectedClean, 'OAG protected cookies')
        }
    },[ logoutStatusOAG ])
    const validateActivationToken = useCallback(() => {
        init(activationToken)
    }, [ init, activationToken ])

    useEffect(() => {
        errorToken && stepNumber === 0 && onWelcomeNext()
    }, [ errorToken ])

    const [ factor, setFactor ] = useState()
    const [ selectedRecoveryType, setSelectedRecoveryType ] = useState('sms')

    // Store phone input so it can be pre-populated when user goes back from verification
    const [ phoneSetupValues, setPhoneSetupValues ] = useState(null)

    // Track where user came from to handle back navigation correctly
    const [ cameFromQrCode, setCameFromQrCode ] = useState(false)

    // Track if user has visited MFA setup to show setup view on back navigation
    const [ visitedMfaSetup, setVisitedMfaSetup ] = useState(false)

    // For HedgeX migration users, fetch account recovery on step 1 to determine if phone setup is needed
    useEffect(() => {
        if (stepNumber === 1 && dataUser?.hedgexMigration && dataUser?.oktaUserId && accountRecoveryStatus === FetchAccountRecovery.DEFAULT) {
            getAccountRecovery({ oktaUserId: dataUser.oktaUserId })
        }
    }, [ stepNumber, dataUser?.hedgexMigration, dataUser?.oktaUserId, accountRecoveryStatus, getAccountRecovery ])

    // For optional-recovery applications, collect and activate a recovery phone
    // when the approved request did not include one.
    useEffect(() => {
        if (stepNumber === 1 && !dataUser?.hedgexMigration && dataUser?.recoveryDetailsOptional && dataUser?.oktaUserId && accountRecoveryStatus === FetchAccountRecovery.DEFAULT) {
            getAccountRecovery({ oktaUserId: dataUser.oktaUserId })
        }
    }, [ stepNumber, dataUser?.hedgexMigration, dataUser?.recoveryDetailsOptional, dataUser?.oktaUserId, accountRecoveryStatus, getAccountRecovery ])

    const requiresOptionalRecoveryPhoneSetup = dataUser?.recoveryDetailsOptional
        && !dataUser?.hedgexMigration
        && accountRecoveryStatus === FetchAccountRecovery.SUCCESS
        && (accountRecoveryData || []).length === 0

    // Fetch phone codes when entering phone setup step
    useEffect(() => {
        if (stepNumber === 1 && dataUser?.hedgexMigration) {
            onRequestPhoneCodes()
        }
    }, [ stepNumber, dataUser?.hedgexMigration, onRequestPhoneCodes ])

    // Fetch phone codes when the optional-recovery journey reaches phone setup.
    useEffect(() => {
        if (stepNumber === 1 && requiresOptionalRecoveryPhoneSetup) {
            onRequestPhoneCodes()
        }
    }, [ stepNumber, requiresOptionalRecoveryPhoneSetup, onRequestPhoneCodes ])


    const handleSelectType = useCallback(type => {
        setSelectedRecoveryType(type)
        onAccountRecoverySelection()
    }, [])

    const handleSetupRecoveryPhone = useCallback(body => {
        setSelectedRecoveryType(body.recoveryType)
        // Store the user's input so it can be pre-populated if they go back
        setPhoneSetupValues({
            countryCode: body.recoveryCountryCode,
            extension: body.recoveryExtension || '',
            phoneNumber: body.recoveryPhoneNumber,
            recoveryType: body.recoveryType,
        })
        onSetupRecoveryPhone(body)
    }, [ onSetupRecoveryPhone ])

    const handleMFA = factor => {
        setFactor(factor)
        setVisitedMfaSetup(true)
        onSetAuthenticator()
    }

    const handleAuthenticatorCode = passCode => {
        const factorType = factor.factorType
        onVerifyAuthenticatorCode({  factorType, passCode })
    }

    const handleQRCodeNext = () => {
        if (factor.factorType === ClientServicingFactorTypes.PUSH) {
            onBackToAuthenticators()
        } else {
            setCameFromQrCode(true)
            onQRCodeNext()
        }
    }

    // Custom back handler for AuthenticatorCode - goes back to QrCode if came from there, otherwise previous step
    const handleAuthenticatorCodeBack = () => {
        if (cameFromQrCode) {
            setCameFromQrCode(false)
            onBackTwoSteps() // Go back 2 steps: 8 -> 6
        } else {
            onBack() // Go back 1 step: 8 -> 7
        }
    }

    const handleSubmitPassword = values => {
        onSubmitPassword({ ...values, isRegistering: true })
    }

    const getStepContent = step => {
        switch (step) {
            case 0:
                return <NewWelcome {...{
                    onNext: validateActivationToken
                }} />
            case 1:
                // For HedgeX migration users, always show phone setup to collect/update recovery details
                if (dataUser?.hedgexMigration) {
                    return <PhoneSetup {...{
                        countryCodeList: phoneCodes || [],
                        error: setupRecoveryPhoneError,
                        initialValues: phoneSetupValues,
                        isProcessing: setupRecoveryPhoneStatus === 'PENDING',
                        onBack,
                        onSubmit: handleSetupRecoveryPhone,
                        userData: dataUser,
                    }} />
                }
                // Optional-recovery applications start without recovery details.
                // Reuse the HedgeX setup, verification, and activation journey.
                if (requiresOptionalRecoveryPhoneSetup) {
                    return <PhoneSetup {...{
                        countryCodeList: phoneCodes || [],
                        error: setupRecoveryPhoneError,
                        initialValues: phoneSetupValues,
                        isProcessing: setupRecoveryPhoneStatus === 'PENDING',
                        onBack,
                        onSubmit: handleSetupRecoveryPhone,
                        userData: dataUser,
                    }} />
                }
                return <RecoverySelection {...{
                    accountRecoveryData,
                    accountRecoveryStatus,
                    getAccountRecovery,
                    onSelectType: handleSelectType,
                    userData: dataUser,
                }} />
            case 2:
                return <Verification {...{
                    changeStep,
                    codeAlreadySent: setupRecoveryPhoneStatus === 'SUCCESS',
                    data: dataSendChallenge || (phoneSetupValues ? { phoneExtension: phoneSetupValues.extension, phoneNumber: `${phoneSetupValues.countryCode}${phoneSetupValues.phoneNumber}` } : {}),
                    error,
                    initSendChallenge,
                    isHedgexUser: !!dataUser?.hedgexMigration,
                    isAAGUser: !!dataUser?.aagMigration,
                    isProcessing: submitStatusVerification === SubmitVerificationTypes.PENDING || submitStatusResendCode === ResendCodeTypes.PENDING,
                    isVerified: submitStatusVerification === SubmitVerificationTypes.SUCCESS,
                    onBack,
                    onNext: onSubmitVerification,
                    onResendCode,
                    resendSuccessMsg,
                    selectedRecoveryType,
                    sendChallengeStatus,
                    verificationError : submitStatusVerification === SubmitVerificationTypes.ERROR
                }} />
            case 3:
                return <SetPassword {...{
                    data: dataUser,
                    error,
                    isProcessing: submitStatusPassword === SubmitPasswordTypes.PENDING,
                    onBack,
                    onNext: handleSubmitPassword
                }} />
            case 4:
                return <Authenticators {...{
                    authenticatorStatus,
                    data: dataAuthenticator,
                    error,
                    getMFAOptionsStatus,
                    initAuthenticators,
                    mfaRequired,
                    onBack,
                    onEnroll,
                    onFinish,
                    onNext: handleMFA,
                    showSetup: visitedMfaSetup
                }} />
            case 5:
                return <DeviceType {...{
                    appName: factor.appName,
                    onBack,
                    onFinish,
                    onNext: onSetDeviceType,
                    provider: factor.provider,
                    title: factor.title,
                    verbiage: factor.deviceTypeVerb
                }} />
            case 6:
                return <QrCode {...{
                    data: dataQRCode,
                    error,
                    factorType: factor.factorType,
                    initQRCode,
                    onBack,
                    onNext: handleQRCodeNext,
                    onQRCodeCannotScan,
                    provider: factor.provider,
                    qrCodeStatus,
                    title: factor.title,
                    verbiage: factor.qrCodeVerb
                }} />
            case 7:
                return <QrCodeCannotScan {...{
                    data: dataQRCode,
                    factorType: factor.factorType,
                    onBack,
                    onNext: onQRCodeCannotScanNext,
                    title: factor.title,
                    verbiage: factor.qrCodeCannotScanVerb
                }} />
            case 8:
                return <AuthenticatorCode {...{
                    error,
                    isProcessing: submitStatusVerifyAuthenticatorStatus === SubmitVerifyAuthenticatorCodeTypes.PENDING,                    
                    onBack: handleAuthenticatorCodeBack,
                    onNext: handleAuthenticatorCode,
                    onSuccess: onAfterVerifyAuthenticatorCodeSuccess,
                    status: submitStatusVerifyAuthenticatorStatus,                    
                    title: factor.title,
                    verbiage: factor.appName
                }} />
            case 9:
                return <SignIn {...{
                    data: dataUserApps,
                    initUserApps,
                    userAppsStatus
                }} />
            default:
                return 'Unknown step'
        }
    }

    if (sessionTimeout) {
        return <SessionTimeout />
    }
    return (
        <Box className={classes.pageWrapper}>
            <AppBar />
            <Box className={classes.contentWrapper}>
                {(status === FetchStatus.PENDING || submitStatusPassword === SubmitPasswordTypes.PENDING) ? (
                    <Box className={classes.gridContainerCenter}>    
                        <Grid md={5} sm={8} xs={12}>
                            <CustomLoader
                                label={translate('loading')}
                                style={{ height: '20vh', marginBottom: '200px', marginTop: '200px' }}
                            />
                        </Grid>
                    </Box>    
                ) : errorToken && stepNumber !== 0 ? (
                    <Box className={classes.gridContainerError}>
                        <ErrorPage errorMessage={errorToken} errorTitle={translate('errorPage.error')} />
                    </Box>
                ) : (<>
                    { (stepNumber > 0 && stepNumber < 9) && <RegistrationBar step={stepNumber} /> }
                    <Box className={classes.gridContainerCenter}>
                        {getStepContent(stepNumber)}
                        
                    </Box>
                        
                </>)}
            </Box>
            <Footer />
        </Box>
    )
}

Component.displayName = 'registration'

Component.defaultProps = {
    stepNumber: 0
}

Component.propTypes = {
    classes: PropTypes.shape({
        loader: PropTypes.object
    }),
    dataAuthenticator: PropTypes.object,
    dataQRCode: PropTypes.object,
    dataSendChallenge: PropTypes.object,
    dataUser: PropTypes.object,
    error: PropTypes.string,
    errorToken: PropTypes.string,
    init: PropTypes.func.isRequired,
    initAuthenticators: PropTypes.func.isRequired,
    initQRCode: PropTypes.func.isRequired,
    initSendChallenge: PropTypes.func.isRequired,
    initUserApps: PropTypes.func.isRequired,
    mfaRequired: PropTypes.bool,
    onBack: PropTypes.func.isRequired,
    onBackToAuthenticators: PropTypes.func.isRequired,
    onBackTwoSteps: PropTypes.func.isRequired,
    onEnroll: PropTypes.func.isRequired,
    onFinish: PropTypes.func.isRequired,
    onQRCodeCannotScan: PropTypes.func.isRequired,
    onQRCodeCannotScanNext: PropTypes.func.isRequired,
    onQRCodeNext: PropTypes.func.isRequired,
    onResendCode: PropTypes.func.isRequired,
    onSetAuthenticator: PropTypes.func.isRequired,
    onSetDeviceType: PropTypes.func.isRequired,
    onSubmitPassword: PropTypes.func.isRequired,
    onSubmitVerification: PropTypes.func.isRequired,
    onVerifyAuthenticatorCode: PropTypes.func.isRequired,
    onWelcomeNext: PropTypes.func.isRequired,
    resendSuccessMsg: PropTypes.string,
    sendChallengeStatus: PropTypes.string,
    sessionTimeout: PropTypes.bool.isRequired,
    stepNumber: PropTypes.number.isRequired,
    userAppsStatus: PropTypes.string
}

export default withStyles(Component, styles)
